// ============================================================================
// UNIFIED SIGN OUT UTILITY
// ============================================================================
//
// Centralized sign out logic. Handles Supabase sign-out, browser storage,
// cookie clearing, and navigation. Used directly via `signOut()` or via the
// `useSignOut()` hook for Next.js router integration.
//
// Implementation helpers live in `./sign-out-internals`.
// ============================================================================

import { useCallback } from "react";
import { useRouter } from "next/navigation";
import {
  DEFAULT_SIGN_OUT_SCOPE,
  scopeEndsCurrentSession,
  type SignOutScope,
} from "@west-tennessee-consulting/supabase";
import { debug } from "@/lib/utils/debug";
import {
  performSupabaseSignOut,
  clearBrowserStorage,
  clearBrowserCookies,
  navigateAfterSignOut,
} from "./sign-out-internals";

export interface SignOutOptions {
  redirectTo?: string;
  redirect?: boolean;
  clearLocalStorage?: boolean;
  clearSessionStorage?: boolean;
  clearCookies?: boolean;
  clearAllAuthCookies?: boolean;
  /**
   * How far the sign-out reaches. Defaults to `"device"` — this browser only.
   * `"others"` and `"everywhere"` are reachable only from the account UI's
   * Devices & sessions card, never from a plain Sign out button.
   */
  scope?: SignOutScope;
}

export interface SignOutResult {
  success: boolean;
  error?: string;
  redirected?: boolean;
}

// ----------------------------------------------------------------------------
// Preset option sets used by the convenience helpers + hook.
// ----------------------------------------------------------------------------

const PRESETS = {
  home: { redirectTo: "/" },
  login: { redirectTo: "/auth/employee" },
  silent: { redirect: false },
  cleanup: {
    redirect: true,
    clearLocalStorage: true,
    clearSessionStorage: true,
    clearCookies: true,
  },
  completeCleanup: {
    redirect: true,
    clearLocalStorage: true,
    clearSessionStorage: true,
    clearAllAuthCookies: true,
  },
} satisfies Record<string, SignOutOptions>;

// ----------------------------------------------------------------------------
// Main sign-out flow
// ----------------------------------------------------------------------------

export async function signOut(options: SignOutOptions = {}): Promise<SignOutResult> {
  const {
    redirectTo = "/",
    clearAllAuthCookies = false,
    scope = DEFAULT_SIGN_OUT_SCOPE,
  } = options;

  // `scope: "others"` keeps the CURRENT session alive — so it must not clear
  // this browser's storage/cookies and must not navigate away, whatever the
  // caller's other flags say. Every other scope ends this session.
  const endsCurrent = scopeEndsCurrentSession(scope);
  const redirect = endsCurrent ? (options.redirect ?? true) : false;
  const clearLocalStorage = endsCurrent ? (options.clearLocalStorage ?? true) : false;
  const clearSessionStorage = endsCurrent ? (options.clearSessionStorage ?? true) : false;
  const clearCookies = endsCurrent ? (options.clearCookies ?? true) : false;

  debug.auth("Starting unified sign out process", { options, scope });

  try {
    const supabaseResult = await performSupabaseSignOut(scope);
    clearBrowserStorage({ clearLocalStorage, clearSessionStorage });
    clearBrowserCookies({
      clearCookies,
      clearAllAuthCookies: endsCurrent && clearAllAuthCookies,
    });

    if (redirect) {
      navigateAfterSignOut(redirectTo);
    }

    const result: SignOutResult = {
      success: supabaseResult.success,
      error: supabaseResult.error,
      redirected: redirect,
    };

    debug.auth("Sign out process completed", result);
    return result;
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : "Unknown error";
    debug.auth("Error in unified sign out process:", error);

    // Even on failure, try to redirect so the user isn't stranded on an
    // authenticated page.
    if (redirect) {
      navigateAfterSignOut(redirectTo);
    }

    return {
      success: false,
      error: errorMessage,
      redirected: redirect,
    };
  }
}

// ----------------------------------------------------------------------------
// Convenience functions (non-hook)
// ----------------------------------------------------------------------------

/**
 * End every OTHER session, keep this one. The account UI's "Sign out other
 * devices" — no redirect, no local cleanup: the user stays signed in here.
 */
export const signOutOtherDevices = (): Promise<SignOutResult> =>
  signOut({ scope: "others", redirect: false });

/**
 * End EVERY session including this one and revoke every refresh token. The
 * credential-compromise action. Also kills MCP/connector sessions by design —
 * only ever call this from an explicit user choice that says so.
 */
export const signOutEverywhere = (
  options: Omit<SignOutOptions, "scope"> = {}
): Promise<SignOutResult> => signOut({ ...options, scope: "everywhere" });

export const signOutToHome = (): Promise<SignOutResult> => signOut(PRESETS.home);
export const signOutToLogin = (): Promise<SignOutResult> => signOut(PRESETS.login);
export const signOutSilent = (): Promise<SignOutResult> => signOut(PRESETS.silent);
export const signOutWithCleanup = (): Promise<SignOutResult> => signOut(PRESETS.cleanup);
export const signOutWithCompleteCleanup = (): Promise<SignOutResult> =>
  signOut(PRESETS.completeCleanup);

// ----------------------------------------------------------------------------
// React hook — same surface as the standalone functions, plus router-based
// navigation when available.
// ----------------------------------------------------------------------------

export function useSignOut() {
  const router = useRouter();

  const signOutWithRouter = useCallback(
    async (options: SignOutOptions = {}) => {
      const result = await signOut(options);

      // `scope: "others"` never navigates — this session survives it.
      if (options.redirect && result.redirected && router) {
        try {
          const targetUrl = options.redirectTo || "/";
          router.push(targetUrl);
          debug.auth(`Router navigation to: ${targetUrl}`);
        } catch (error) {
          debug.auth(
            "Router navigation failed, falling back to window.location:",
            error
          );
          navigateAfterSignOut(options.redirectTo);
        }
      }

      return result;
    },
    [router]
  );

  return {
    signOut: signOutWithRouter,
    /** Other devices only; this session survives (account UI). */
    signOutOtherDevices: () => signOutWithRouter({ scope: "others", redirect: false }),
    /** Every session incl. this one + refresh-token revocation (account UI). */
    signOutEverywhere: () =>
      signOutWithRouter({ ...PRESETS.home, scope: "everywhere" }),
    signOutToHome: () => signOutWithRouter(PRESETS.home),
    signOutToLogin: () => signOutWithRouter(PRESETS.login),
    signOutSilent: () => signOutWithRouter(PRESETS.silent),
    signOutWithCleanup: () => signOutWithRouter(PRESETS.cleanup),
    signOutWithCompleteCleanup: () => signOutWithRouter(PRESETS.completeCleanup),
  };
}
